import React from 'react';
import './Privacy.css';

export default function Privacy() {
  return (
    <div className="privacy-container">
      <div className="privacy-content">
        <button
          onClick={() => {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            window.location.href = token ? `/?token=${token}` : '/';
          }}
        >
          ← Back
        </button>
        <h1>Your Privacy Matters</h1>

        <p className="intro-text">
          This Privacy Policy explains how PhomoCam collects, uses, stores, and protects your personal information, including biometric data from facial recognition technology. By using PhomoCam, you consent to the collection and processing of your data as described in this policy.
        </p>

        <section>
          <h2>1. Information We Collect</h2>

          <div className="subsection">
            <h3>Account Information:</h3>
            <ul>
              <li>Email address and username</li>
              <li>Profile information you provide</li>
              <li>Authentication credentials (encrypted)</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Biometric Data (Special Category):</h3>
            <ul>
              <li>Facial recognition data from your selfie enrollment</li>
              <li>Face vectors and mathematical representations (not actual images)</li>
              <li>Facial geometry measurements and unique facial templates</li>
              <li>This data is used solely for identifying you in photos</li>
              <li>You may request deletion at any time</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Content:</h3>
            <ul>
              <li>Photos you upload or capture</li>
              <li>Photos you choose to import from Apple iCloud Photos into the app</li>
              <li>Photos you choose to sync or export from the app back to Apple iCloud Photos</li>
              <li>Metadata from photos (date, time, camera settings, device type, OS version)</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Usage Data:</h3>
            <ul>
              <li>App interactions and features used</li>
              <li>Device information (model, OS version)</li>
              <li>Push notification tokens</li>
              <li>Analytics data to help improve the service</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>2. Legal Basis for Processing</h2>
          <p>
            For users in jurisdictions requiring it (such as the EU under GDPR), we process your personal data based on:
          </p>
          <ul>
            <li>Your explicit consent (required for biometric data processing)</li>
            <li>Performance of a contract (providing the app's features)</li>
            <li>Compliance with legal obligations (data retention, law enforcement)</li>
            <li>Our legitimate interests (security, preventing abuse, service improvement)</li>
            <li>Vital interests (safety and security of users)</li>
            <li>Some data management features, such as account deletion or export, may be limited while we enhance the app. We aim to provide full control over your data in future updates.</li>
          </ul>
          <p>You may withdraw consent at any time, though this may limit service functionality.</p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Identify you in photos using facial recognition</li>
            <li>Share photos with friends you've authorized</li>
            <li>Send notifications about shared photos and event invites</li>
            <li>Improve app performance and user experience</li>
            <li>Ensure security and prevent unauthorized access</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Facial Recognition Technology</h2>

          <div className="subsection">
            <h3>How It Works:</h3>
            <ul>
              <li>We use AWS Rekognition to process facial data</li>
              <li>Your selfie is converted to mathematical vectors</li>
              <li>These vectors are compared to identify you in photos</li>
              <li>Original selfies are deleted after processing</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Your Control & Biometric Rights:</h3>
            <ul>
              <li>You can update your facial data anytime in settings</li>
              <li>You maintain full control over this biometric data</li>
              <li>Right to request deletion of biometric identifiers</li>
              <li>Written notice provided before collection of biometric data</li>
              <li>No sale of biometric data to third parties (prohibited)</li>
              <li>Biometric data not disclosed without consent or court order</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>5. Data Storage & Security</h2>

          <div className="subsection">
            <h3>Where We Store Data:</h3>
            <ul>
              <li>Photos: Amazon S3 with encryption at rest</li>
              <li>User data: Amazon DynamoDB with encryption</li>
              <li>Face vectors: AWS Rekognition collections</li>
              <li>All data is stored in secure AWS data centers</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Security Measures:</h3>
            <ul>
              <li>End-to-end encryption for sensitive data</li>
              <li>Secure HTTPS connections for all transfers</li>
              <li>Access controls and authentication</li>
              <li>Regular security audits and updates</li>
              <li>Automatic backups with encryption</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Security Disclaimer:</h3>
            <p>
              While we implement industry-standard security measures including encryption, access controls, and regular audits, no method of transmission or storage is completely secure. We cannot guarantee absolute security but commit to:
            </p>
            <ul>
              <li>Immediate notification of security breaches affecting your data</li>
              <li>Annual security assessments and penetration testing</li>
              <li>SOC 2 Type II compliance for our infrastructure</li>
              <li>Zero-knowledge architecture where technically feasible</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>6. Data Sharing & Third Parties</h2>

          <div className="subsection">
            <h3>We DO NOT:</h3>
            <ul>
              <li>Sell your personal data to third parties</li>
              <li>Share your facial recognition data with advertisers</li>
              <li>Use your photos for marketing without permission</li>
              <li>Allow unauthorized access to your content</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>We MAY share data:</h3>
            <ul>
              <li>With friends you explicitly authorize</li>
              <li>When required by law, legal process, or court order</li>
              <li>To protect safety and prevent harm</li>
              <li>With trusted service providers under strict data processing agreements:
                <ul>
                  <li>Amazon Web Services (cloud infrastructure)</li>
                  <li>Expo Application Services (app distribution)</li>
                  <li>Push notification providers</li>
                  <li>Apple Inc. (iCloud Photos access for importing/syncing and only photos you explicitly select for import/export, subject to Apple's privacy and security practices)</li>
                </ul>
              </li>
              <li>In case of merger, acquisition, or sale of assets (with notice)</li>
              <li>With law enforcement when legally required</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>7. Your Rights & Choices</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access all data we have about you (within 30 days)</li>
            <li>Request correction of inaccurate data</li>
            <li>Export your data in a portable format</li>
            <li>Request deletion of your account and all associated data</li>
            <li>Opt-out of notifications and marketing communications</li>
            <li>Control who can see your photos and biometric matches</li>
            <li>File complaints with relevant data protection authorities</li>
            <li>Receive compensation for unauthorized use of biometric data (where applicable)</li>
          </ul>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <ul>
            <li>Active accounts: Data retained while account is active</li>
            <li>Facial/biometric data: Only as long as necessary to provide our services</li>
            <li>Biometric data: Immediate deletion upon request or account closure</li>
            <li>Legal holds: Data may be retained if required by law (with notice)</li>
            <li>Photos: Deleted immediately upon user request</li>
          </ul>
        </section>

        <section>
          <h2>9. Children's Privacy</h2>
          <p>
            PhomoCam is not intended for children under 13. We do not knowingly collect data from children under 13. In jurisdictions where a higher minimum age applies, we comply with that standard. If we become aware that we have inadvertently collected personal information from a child under 13 without verified parental consent, we will delete such information promptly. If you believe a child has provided us with personal information, please contact us immediately for removal.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable laws.
          </p>
        </section>

        <section>
          <h2>11. California Privacy Rights (CCPA)</h2>
          <p>California residents have additional rights including:</p>
          <ul>
            <li>Right to know what personal information is collected and how it's used</li>
            <li>Right to know if information is sold or disclosed (we do not sell)</li>
            <li>Right to opt-out of sale of personal information</li>
            <li>Right to non-discrimination for exercising privacy rights</li>
            <li>Right to request deletion of personal information</li>
            <li>Right to correct inaccurate personal information</li>
            <li>Special protections for biometric data under CCPA</li>
          </ul>
          <p><strong>Note:</strong> We do not sell personal information to third parties.</p>
        </section>

        <section>
          <h2>12. European Privacy Rights (GDPR)</h2>
          <p>If you are in the European Economic Area, you have rights under GDPR including:</p>
          <ul>
            <li>Right to be informed about data processing</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Rights related to automated decision making</li>
          </ul>
        </section>

        <section>
          <h2>13. Changes to Service and This Policy</h2>
          <p>
            We may update this Privacy Policy or make changes to our data practices as we add new features or integrate with additional third-party services. We will notify you of any material changes through the app or via email. Your continued use after changes indicates acceptance of the updated policy.
          </p>
        </section>

        <section>
          <h2>14. Data Breach Notification</h2>
          <p>In the event of a data breach that affects your personal information:</p>
          <ul>
            <li>We will notify affected users within 72 hours of discovery</li>
            <li>Notification will include nature of breach and data involved</li>
            <li>We will report to relevant authorities as required by law</li>
            <li>We maintain cyber liability insurance to protect against breaches</li>
            <li>Incident response plan includes forensic investigation and remediation</li>
          </ul>
        </section>

        <section>
          <h2>15. State-Specific Biometric Privacy Laws</h2>
          <p>For residents of Illinois, Texas, and Washington:</p>
          <ul>
            <li>Written consent obtained before biometric data collection</li>
            <li>Disclosure of retention schedule and destruction procedures</li>
            <li>No sale of biometric data (prohibited)</li>
            <li>Right to request destruction of biometric identifiers</li>
            <li>Statutory damages available for violations</li>
            <li>Biometric data stored separately from other personal information</li>
          </ul>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>
            For questions about your privacy or data handling, please visit our <a href={`/support${window.location.search ? window.location.search + '&from=privacy' : '?from=privacy'}`}>Support Page</a> or contact us directly at: <a href="mailto:prasiddha@gmail.com">prasiddha@gmail.com</a>.
          </p>
        </section>

        <p className="last-updated">Last updated: September 4th, 2025</p>
      </div>

      {/* Footer */}
      <div className="privacy-footer">
        © 2025 PhomoCam. Share memories, not just photos.
      </div>
    </div>
  );
}